/**
 * HUD parts shared by the layouts, and the references the HUD updates. Markup mirrors the concept board's (frames
 * 01 and 04) element for element, so the same CSS values give the same pixels.
 */
import { ABILITIES } from './chips.ts';
import { add, crosshairIcon, el, pauseIcon, settingsIcon } from './dom.ts';

export interface HudRefs {
  /** Contents of the panels frame and the controls frame. */
  panels: HTMLElement[];
  controls: HTMLElement[];
  score: HTMLElement;
  high: HTMLElement | null;
  /** Gets `hw-is-low` below 30% fuel. */
  fuelBox: HTMLElement;
  fuelPct: HTMLElement;
  fuelFill: HTMLElement;
  lowFuel: HTMLElement;
  /** Landscape: one icon per jet. Portrait: the "JETS III" chip. */
  jetIcons: SVGSVGElement[];
  jetsText: HTMLElement | null;
  level: HTMLElement;
  section: HTMLElement;
  /** Portrait: the section chip hides while the LOW FUEL chip shows. */
  sectionYields: boolean;
  bridgeFrom: HTMLElement | null;
  bridgeTo: HTMLElement | null;
  progress: HTMLElement;
  abilities: HTMLElement;
  abilityChips: HTMLElement[];
  alert: HTMLElement;
  bannerWrap: HTMLElement;
  banner: HTMLElement;
  bannerHead: HTMLElement;
  bannerSub: HTMLElement;
  bannerRows: HTMLElement;
  bannerFinal: HTMLElement;
  bannerHigh: HTMLElement;
  bannerNew: HTMLElement;
  title: HTMLElement;
  press: HTMLElement;
  /** Title-screen credits. */
  credit: HTMLElement;
  /** Pause, or settings on the title screen, where the layout has one corner button (touch, portrait). */
  menuBtn: HTMLButtonElement | null;
  pauseBtn: HTMLButtonElement | null;
  settingsBtn: HTMLButtonElement | null;
  pad: HTMLElement | null;
  thumb: HTMLElement | null;
  fire: HTMLElement | null;
  fireBtn: HTMLButtonElement | null;
  autoFire: HTMLElement | null;
}

/** Frame 01's score panel. */
export function scorePanel(doc: Document): { box: HTMLElement; score: HTMLElement; high: HTMLElement } {
  const box = el(doc, 'div', 'hw-score hw-glass');
  const label = add(box, el(doc, 'div', 'hw-score-label'));
  add(label, el(doc, 'span', '', 'SCORE'), el(doc, 'span', 'hw-dot'));
  const score = add(box, el(doc, 'div', 'hw-score-value', '000 000'));
  const high = add(box, el(doc, 'div', 'hw-score-high', 'HIGH · 000 000'));
  return { box, score, high };
}

/** Frame 01's fuel panel. */
export function fuelPanel(doc: Document): { box: HTMLElement; pct: HTMLElement; fill: HTMLElement; low: HTMLElement } {
  const box = el(doc, 'div', 'hw-fuel hw-glass');
  const head = add(box, el(doc, 'div', 'hw-fuel-head'));
  add(head, el(doc, 'span', '', 'FUEL'));
  const low = add(head, el(doc, 'span', 'hw-low', 'LOW FUEL'));
  low.hidden = true;
  const pct = add(head, el(doc, 'span', 'hw-fuel-pct', '100%'));
  const row = add(box, el(doc, 'div', 'hw-fuel-row'));
  add(row, el(doc, 'span', '', 'E'));
  const bar = add(row, el(doc, 'div', 'hw-fuel-bar'));
  const fill = add(bar, el(doc, 'div', 'hw-fuel-fill'));
  fill.style.width = '100%';
  add(bar, el(doc, 'div', 'hw-fuel-seg'));
  add(row, el(doc, 'span', '', 'F'));
  return { box, pct, fill, low };
}

/** Enemy-ability chips: one per ability, shown or hidden by level. */
export function abilityGroup(doc: Document, label: boolean): { box: HTMLElement; chips: HTMLElement[] } {
  const box = el(doc, 'div', 'hw-abilities');
  if (label) add(box, el(doc, 'div', 'hw-abilities-label', 'ENEMY ABILITIES'));
  const row = add(box, el(doc, 'div', 'hw-chips'));
  const chips = ABILITIES.map((a) => {
    const c = add(row, el(doc, 'span', a.fire ? 'hw-chip hw-threat' : 'hw-chip', a.text));
    c.hidden = true;
    return c;
  });
  box.hidden = true;
  return { box, chips };
}

export function alertChip(doc: Document): HTMLElement {
  const a = el(doc, 'div', 'hw-alert', 'BRIDGE GUNS TRACKING · DESTROY IT NOW');
  a.setAttribute('role', 'alert');
  a.hidden = true;
  return a;
}

/** The banner (§7.3), centred in the panels frame. */
export function bannerPart(doc: Document) {
  const wrap = el(doc, 'div', 'hw-banner-wrap');
  wrap.setAttribute('aria-live', 'polite');
  const banner = add(wrap, el(doc, 'div', 'hw-banner'));
  const head = add(banner, el(doc, 'div', 'hw-banner-head'));
  const sub = add(banner, el(doc, 'div', 'hw-banner-sub'));
  const rows = add(banner, el(doc, 'div', 'hw-banner-rows'));
  const finalRow = add(rows, el(doc, 'span', '', 'FINAL '));
  const final = add(finalRow, el(doc, 'b', '', '000 000'));
  const highRow = add(rows, el(doc, 'span', '', 'HIGH '));
  const high = add(highRow, el(doc, 'b', '', '000 000'));
  const fresh = add(banner, el(doc, 'div', 'hw-banner-new', 'NEW HIGH SCORE'));
  wrap.hidden = true;
  return { wrap, banner, head, sub, rows, final, high, fresh };
}

/** The copyright year on the title screen. */
export const COPYRIGHT_YEAR = 2026;

/** Frame 01's title block and press pill. */
export function titleParts(doc: Document): { title: HTMLElement; press: HTMLElement; credit: HTMLElement } {
  const title = el(doc, 'div', 'hw-title hw-anim');
  add(
    title,
    el(doc, 'div', 'hw-title-mark', 'HEADWATER'),
    el(doc, 'div', 'hw-title-sub', 'FLY THE RIVER TO ITS SOURCE'),
  );
  const press = el(doc, 'div', 'hw-press', 'PRESS SPACE OR TAP TO FLY');
  // Credits on the title screen (Mehdi, 2026-10-10).
  const credit = el(doc, 'div', 'hw-credit');
  add(
    credit,
    el(doc, 'div', 'hw-credit-by', 'DESIGNED & DEVELOPED BY ITSMEKAVEH'),
    el(doc, 'div', 'hw-credit-copy', `© ${COPYRIGHT_YEAR} ITSMEKAVEH · ALL RIGHTS RESERVED`),
  );
  return { title, press, credit };
}

/** A 44 px glass button (§7.2): frame 04's pause button with the §3.9 glass (D15). */
export function iconButton(doc: Document, kind: 'pause' | 'settings', label: string): HTMLButtonElement {
  const b = el(doc, 'button', 'hw-btn hw-glass');
  b.type = 'button';
  b.setAttribute('aria-label', label);
  b.append(kind === 'pause' ? pauseIcon(doc) : settingsIcon(doc));
  return b;
}

/** Swaps a corner button between pause (in a run) and settings (title screen). */
export function setButtonKind(doc: Document, b: HTMLButtonElement, kind: 'pause' | 'settings'): void {
  if (b.dataset.kind === kind) return;
  b.dataset.kind = kind;
  b.setAttribute('aria-label', kind === 'pause' ? 'Pause' : 'Settings');
  b.replaceChildren(kind === 'pause' ? pauseIcon(doc) : settingsIcon(doc));
}

/** Frame 04's steering pad, fire button and auto-fire label. */
export function touchControls(doc: Document) {
  const pad = el(doc, 'div', 'hw-pad');
  pad.setAttribute('aria-label', 'Steering pad: drag to steer, up and down for throttle');
  const ring = add(pad, el(doc, 'div', 'hw-ring'));
  const thumb = add(ring, el(doc, 'div', 'hw-thumb'));
  add(pad, el(doc, 'div', 'hw-pad-label', 'DRAG TO STEER\nUP / DOWN = THROTTLE'));
  const fire = el(doc, 'div', 'hw-fire');
  add(fire, el(doc, 'div', 'hw-fire-ring'));
  const fireBtn = add(fire, el(doc, 'button', 'hw-fire-btn'));
  fireBtn.type = 'button';
  fireBtn.setAttribute('aria-label', 'Fire');
  fireBtn.append(crosshairIcon(doc), el(doc, 'span', '', 'FIRE'));
  const autoFire = el(doc, 'div', 'hw-autofire', 'AUTO-FIRE · ON');
  return { pad, thumb, fire, fireBtn, autoFire };
}
